import { ClassCompTable } from 'components/ClassComp';
import { ButtonIconAction } from 'components/button/ButtonIconAction';
import { StatusTag } from 'components/tag/StatusTag';
import { TagComponent } from 'components/tag/TagComponent';
import { SubTitle } from 'components/text/SubTitle';
import { Checkbox, Select, Typography, formatDate, openModal } from 'enable-ui';

import { RoleKey, TrainerRoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { FunctionalTestMethod } from 'modules/dashboard/core/enum';
import type { Learner } from 'modules/learners/core/type';

import { SelectStyle } from 'styles/select';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as ReportIcon } from 'assets/icons/report-analytics.svg';
import { ReactComponent as VideoIcon } from 'assets/icons/video.svg';

import { checkRole } from 'helpers';
import { get } from 'lodash';

import { COLOR_CONFIG } from 'constants/color';

import { getStyleClassCompletion } from './helpers';

export const getClassesColumns = (
  onClickAction: (key: 'delete' | 'update-status', id: string) => void,
) => {
  const isInstitutionAdmin = checkRole([RoleKey.INSTITUTION_ADMIN]);
  const columns = [
    {
      id: 'name',
      Header: 'Class',
      width: isInstitutionAdmin ? '100%' : '60%',
      minWidth: isInstitutionAdmin ? '100%' : '60%',
      sort: true,
      sortType: 'name',
      Cell: ({ ...props }) => {
        const code = props.data[props.row.index]?.code;

        const name = props.data[props.row.index]?.name;

        const thumbnail = props.data[props.row.index]?.thumbnail;

        return <ClassCompTable name={name} code={code} thumbnail={thumbnail ?? ''} />;
      },
    },
  ];

  if (checkRole([RoleKey.BIOLOGIC_ADMIN])) {
    columns.push({
      id: 'institution',
      Header: 'Institution',
      width: '40%',
      minWidth: '40%',
      sort: true,
      sortType: 'institution',
      Cell: ({ ...props }) => {
        const institution = props.data[props.row.index]?.institution?.name || '';

        return <Typography>{institution}</Typography>;
      },
    });
  }

  if (isInstitutionAdmin) {
    columns.push(
      {
        id: 'totalLearner',
        Header: 'Clients',
        width: '134px',
        minWidth: '134px',
        sort: false,
        sortType: 'totalLearner',
        Cell: ({ ...props }) => {
          const totalLearner = props.data[props.row.index]?.totalLearner;

          return <Typography>{totalLearner} clients</Typography>;
        },
      },
      {
        id: 'duration',
        Header: 'Duration',
        width: '167px',
        minWidth: '167px',
        sort: false,
        sortType: 'duration',
        Cell: ({ ...props }) => {
          const startDate = props.data[props.row.index]?.startDate;

          const endDate = props.data[props.row.index]?.endDate;

          if (!startDate || !endDate) {
            return <></>;
          }

          return (
            <Typography>{`${formatDate(startDate, 'dd MMM')} - ${formatDate(endDate, 'dd MMM')}`}</Typography>
          );
        },
      },
    );
  }

  columns.push(
    {
      id: 'isCompleted',
      Header: 'Class completion',
      width: '154px',
      minWidth: '154px',
      sort: true,
      sortType: 'isCompleted',
      Cell: ({ ...props }) => {
        const classCompletion = props.data[props.row.index]?.classCompletion;

        const { color } = getStyleClassCompletion(classCompletion);

        return <TagComponent text={classCompletion} color={color} />;
      },
    },
    {
      id: 'status',
      Header: 'Status',
      width: '103px',
      minWidth: '103px',
      sort: true,
      sortType: 'status',
      Cell: ({ ...props }) => {
        const isActive = props.data[props.row.index]?.isActive;

        return <StatusTag isActive={isActive} />;
      },
    },
  );

  if (!checkRole([RoleKey.TRAINER_BIOLOGIC, RoleKey.TRAINER_INSTITUTION])) {
    columns.push({
      id: 'action',
      Header: '',
      width: '40px',
      minWidth: '40px',
      sort: false,
      sortType: 'name',
      Cell: ({ ...props }) => {
        const classId = props.data[props.row.index]?.id;

        const isActive = props.data[props.row.index]?.isActive;

        return (
          <ButtonIconAction
            actions={[
              {
                label: 'Delete',
                onClick: () => onClickAction('delete', classId),
              },
              isActive
                ? {
                    label: 'Inactivate',
                    onClick: () => onClickAction('update-status', classId),
                  }
                : {
                    label: 'Activate',
                    onClick: () => onClickAction('update-status', classId),
                  },
            ]}
          />
        );
      },
    });
  }

  return columns;
};

export const getTrainersInClasses = (
  selectedTrainers: Record<string, string>,
  onChangeSelectedTrainers: (value: string) => () => void,
) => {
  return [
    {
      id: 'selected',
      width: 20,
      maxWidth: 20,
      minWidth: 20,
      Cell: ({ ...props }) => {
        return (
          <Checkbox
            checked={Boolean(selectedTrainers[props.data[props.row.index]['id']])}
            onClick={onChangeSelectedTrainers(props.data[props.row.index]['id'])}
          />
        );
      },
    },
    {
      id: 'name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        const role = props.data[props.row.index]?.trainerRole?.name;

        return (
          <div>
            <Typography
              style={{
                textAlign: 'start',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: 200,
              }}
              variant="h5"
              weight={500}
            >
              {name}
            </Typography>
            <Typography style={{ textAlign: 'start' }} variant="body-2">
              {role}
            </Typography>
          </div>
        );
      },
    },
  ];
};

export const getTrainersColumn = (onDelete: (index: number) => void) => {
  return [
    {
      id: 'name',
      Header: 'Name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        return (
          <div>
            <Typography style={{ textAlign: 'start' }} variant="h5">
              {name}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'role',
      Header: 'Role',
      width: '250px',
      minWidth: '250px',
      Cell: ({ ...props }) => {
        const role = props.data[props.row.index]?.trainerRole?.name;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {role}
          </Typography>
        );
      },
    },

    {
      id: 'id',
      Header: '',
      width: '50px',
      minWidth: '50px',
      Cell: ({ ...props }) => {
        return (
          <CloseIcon style={{ cursor: 'pointer' }} onClick={() => onDelete(props.row.index)} />
        );
      },
    },
  ];
};

export const getTrainersInMembersColumn = (
  onClickAction: (
    key: 'view' | 'delete',
    id: string,
    userClassId?: string,
    type?: 'trainer' | 'learner',
  ) => void,
  disabledRemoveTrainer: boolean,
) => {
  const columns = [
    {
      id: 'name',
      Header: 'Name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        return (
          <div>
            <Typography style={{ textAlign: 'start' }} variant="h5">
              {name}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'role',
      Header: 'Role',
      width: '250px',
      minWidth: '250px',
      Cell: ({ ...props }) => {
        const role = props.data[props.row.index]?.trainerRole?.name;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {role}
          </Typography>
        );
      },
    },
  ];

  if (!checkRole([RoleKey.TRAINER_BIOLOGIC, RoleKey.TRAINER_INSTITUTION])) {
    columns.push({
      id: 'action',
      Header: '',
      width: '50px',
      minWidth: '50px',
      Cell: ({ ...props }) => {
        const trainerId = props.data[props.row.index]?.id;

        const userClassId = props.data[props.row.index]?.userClassId;

        const role = props.data[props.row.index]?.trainerRole?.key;

        return (
          <ButtonIconAction
            actions={[
              {
                label: 'View profile',
                onClick: () => onClickAction('view', trainerId, '', 'trainer'),
              },
              {
                label: 'Remove',
                onClick: () => onClickAction('delete', trainerId, userClassId),
                disabled:
                  (role === TrainerRoleKey.TRAIN_THE_TRAINER ||
                    role === TrainerRoleKey.COACH_MANAGER) &&
                  disabledRemoveTrainer,
              },
            ]}
          />
        );
      },
    });
  }

  return columns;
};

export const getLearnersInMembersColumn = (
  onClickAction: (
    key: 'view' | 'delete',
    id: string,
    userClassId?: string,
    type?: 'trainer' | 'learner',
  ) => void,
  goToLearnScorePage: (learnerId: string) => void,
  groupLearners: { id: string; index: number; group: string; learners: Learner[] }[],
  handleAddLearnerToGroup: (groupId: string, userId: string) => void,
) => {
  return [
    {
      id: 'name',
      Header: 'Name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        return (
          <div>
            <Typography style={{ textAlign: 'start' }} variant="h5">
              {name}
            </Typography>
          </div>
        );
      },
    },
    {
      id: 'score',
      Header: 'Score',
      width: '250px',
      minWidth: '250px',
      Cell: ({ ...props }) => {
        const score = props.data[props.row.index]?.score;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {score}
          </Typography>
        );
      },
    },
    {
      id: 'rank',
      Header: 'Rank',
      width: '250px',
      minWidth: '250px',
      Cell: ({ ...props }) => {
        const rank = props.data[props.row.index]?.rank;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            #{rank}
          </Typography>
        );
      },
    },
    {
      id: 'group',
      Header: 'Group',
      width: '150px',
      minWidth: '150px',
      Cell: ({ ...props }) => {
        const group = props.data[props.row.index]?.userClassGroupId;

        return (
          <SelectStyle>
            <Select
              options={[
                { label: 'No group', key: '' },
                ...groupLearners
                  .map((el, elIndex) => ({
                    label: `Group ${elIndex}`,
                    key: el.id,
                    learners: el.learners,
                  }))
                  .filter((el) => el.key && (el.key === group || el.learners.length < 4)),
              ]}
              selectedKeys={group ? group : ''}
              onChange={(value: any) =>
                handleAddLearnerToGroup(value?.key, props.data[props.row.index]?.id)
              }
              inputStyle={{
                width: '150px',
                border: 'none',
                backgroundColor:
                  (props.row.index + 1) % 2 === 0 ? COLOR_CONFIG.doctor : COLOR_CONFIG.white,
              }}
              containerStyle={{ maxWidth: 150 }}
            />
          </SelectStyle>
        );
      },
    },
    {
      id: 'report',
      Header: '',
      width: '50px',
      minWidth: '50px',
      Cell: ({ ...props }) => {
        const learnId = props.data[props.row.index]?.id;

        return (
          <ReportIcon onClick={() => goToLearnScorePage(learnId)} style={{ cursor: 'pointer' }} />
        );
      },
    },
    {
      id: 'action',
      Header: '',
      width: '50px',
      minWidth: '50px',
      Cell: ({ ...props }) => {
        const learnerId = props.data[props.row.index]?.id;

        const userClassId = props.data[props.row.index]?.userClassId;

        return (
          <ButtonIconAction
            actions={[
              {
                label: 'View profile',
                onClick: () => onClickAction('view', learnerId, '', 'learner'),
              },
              {
                label: 'Remove',
                onClick: () => onClickAction('delete', learnerId, userClassId),
              },
            ]}
          />
        );
      },
    },
  ];
};

export const getLessonExercisesColumn = (
  isSupervised: boolean,
  setIndex: (value: number) => void,
  setLevel: (value: number) => void,
) => {
  const columns = [
    {
      id: 'id',
      Header: '#',
      width: '50px',
      minWidth: '50px',

      Cell: ({ ...props }) => {
        const index = props.row.index + 1;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {index}
          </Typography>
        );
      },
    },
    {
      id: 'name',
      Header: 'Exercise',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const rowData = props.data?.[props.row.index] ?? {};

        const name = get(rowData, 'exercise.name', rowData.name);
        const category = get(rowData, 'exercise.category.name', rowData.category?.name);
        const thumbnail = get(rowData, 'exercise.thumbnail', rowData.thumbnail);
        const upperBody = get(rowData, 'exercise.upperBody', rowData.upperBody);
        const lowerBody = get(rowData, 'exercise.lowerBody', rowData.lowerBody);
        const level = get(rowData, 'exercise.level', rowData.level);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={thumbnail} style={{ height: 48, width: 48, borderRadius: 12 }} />
            <div>
              <Typography style={{ textAlign: 'start' }} variant="h5">
                {name}
              </Typography>
              <Typography style={{ fontSize: '16px', lineHeight: '22px', color: '#808080' }}>
                {category}
              </Typography>

              <div style={{ display: 'flex' }}>
                {(upperBody || lowerBody) && (
                  <Typography
                    style={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontWeight: 400,
                      color: '#808080',
                      marginRight: 12,
                      minWidth: 162,
                    }}
                  >
                    {upperBody}% UPPER | {lowerBody}% LOWER
                  </Typography>
                )}

                {level && (
                  <Typography
                    style={{
                      fontWeight: 600,
                      background: 'rgba(0, 0, 0, 50%)',
                      color: '#FFFFFF',
                      padding: '0 8px',
                      fontSize: 14,
                      borderRadius: '4px',
                      height: '22px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Client type {level}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'set',
      Header: 'Set',
      width: '100px',
      minWidth: '100px',
      Cell: ({ ...props }) => {
        const set = props.data[props.row.index]?.sets;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {set}
          </Typography>
        );
      },
    },
    {
      id: 'rep',
      Header: 'Rep',
      width: '100px',
      minWidth: '100px',
      Cell: ({ ...props }) => {
        const rep = props.data[props.row.index]?.reps;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {rep}
          </Typography>
        );
      },
    },
    {
      id: 'duration',
      Header: 'Set duration',
      width: '150px',
      minWidth: '150px',
      Cell: ({ ...props }) => {
        const duration = props.data[props.row.index]?.duration;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {duration}s
          </Typography>
        );
      },
    },
    {
      id: 'score',
      Header: 'Max score',
      width: '100px',
      minWidth: '100px',
      Cell: ({ ...props }) => {
        const set = props.data[props.row.index]?.sets;

        const rep = props.data[props.row.index]?.reps;

        const score = Number(set) * Number(rep);

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {score}
          </Typography>
        );
      },
    },
  ];

  if (isSupervised) {
    columns.push({
      id: 'video ',
      Header: 'Video',
      width: '62px',
      minWidth: '62px',
      Cell: ({ ...props }) => {
        return (
          <div
            style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
            onClick={() => {
              setLevel(props.data[props.row.index].level);
              openModal(MODAL_KEY.SHOW_VIDEO);
              setIndex(props.row.index);
            }}
          >
            <VideoIcon />
          </div>
        );
      },
    });
  }

  return columns;
};

export const getFunctionalTestColumn = () => {
  return [
    {
      id: 'id',
      Header: '#',
      width: '50px',
      minWidth: '50px',

      Cell: ({ ...props }) => {
        const index = props.row.index + 1;

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {index}
          </Typography>
        );
      },
    },
    {
      id: 'name',
      Header: 'Functional test',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const rowData = props.data?.[props.row.index] ?? {};

        const name = get(rowData, 'functionalTest.name', rowData.name);
        const thumbnail = get(rowData, 'functionalTest.thumbnail', rowData.thumbnail);
        const type = get(rowData, 'functionalTest.type', rowData.type);
        const method = get(rowData, 'functionalTest.method', rowData.method);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={thumbnail} style={{ height: 48, width: 48, borderRadius: 12 }} />
            <div>
              <Typography style={{ textAlign: 'start' }} variant="h5">
                {name}
              </Typography>
              {type ? (
                <SubTitle leftTitle={'Score entry'} rightTitle={type} />
              ) : (
                <Typography
                  style={{
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontWeight: 400,
                    color: '#808080',
                  }}
                >
                  {method?.toLowerCase() === FunctionalTestMethod.Kg
                    ? 'Weight entry'
                    : method?.toLowerCase() === FunctionalTestMethod.Time
                      ? 'Time entry'
                      : 'Rep count entry'}
                </Typography>
              )}
            </div>
          </div>
        );
      },
    },

    {
      id: 'duration',
      Header: 'Threshold time',
      width: '150px',
      minWidth: '150px',
      Cell: ({ ...props }) => {
        const rowData = props.data?.[props.row.index] ?? {};
        const duration = get(rowData, 'functionalTest.thresholdTime', rowData.thresholdTime);

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {duration ? `${Number(duration) / 1000} s` : ''}
          </Typography>
        );
      },
    },
    {
      id: 'score',
      Header: 'Max score',
      width: '200px',
      minWidth: '200px',
      Cell: ({ ...props }) => {
        const rowData = props.data?.[props.row.index] ?? {};
        const score = get(rowData, 'functionalTest.maxScore', rowData.maxScore);

        return (
          <Typography style={{ textAlign: 'start' }} variant="h5">
            {score}
          </Typography>
        );
      },
    },
  ];
};

export const getMembersInClassColumn = (
  onChangeSelectedMembers: (value: string) => () => void,
  selectedMembers: Record<string, string>,
) => {
  return [
    {
      id: 'selected',
      width: 20,
      maxWidth: 20,
      minWidth: 20,
      Cell: ({ ...props }) => {
        return (
          <Checkbox
            checked={Boolean(selectedMembers[props.data[props.row.index]['id']])}
            onClick={onChangeSelectedMembers(props.data[props.row.index]['id'])}
          />
        );
      },
    },
    {
      id: 'name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        const email = props.data[props.row.index]?.trainerRole?.name;

        return (
          <div>
            <Typography style={{ textAlign: 'start' }} weight={700}>
              {name}
            </Typography>
            <Typography
              style={{
                textAlign: 'start',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: 200,
              }}
              variant="h5"
            >
              {email}
            </Typography>
          </div>
        );
      },
    },
  ];
};

export const getLessonsInClassColumn = (
  onChangeSelectedLessons: (value: string) => () => void,
  selectedLessons: Record<string, string>,
) => {
  return [
    {
      id: 'selected',
      width: 20,
      maxWidth: 20,
      minWidth: 20,
      Cell: ({ ...props }) => {
        return (
          <Checkbox
            checked={Boolean(selectedLessons[props.data[props.row.index]['id']])}
            onClick={onChangeSelectedLessons(props.data[props.row.index]['id'])}
          />
        );
      },
    },
    {
      id: 'name',
      width: '100%',
      minWidth: '100%',

      Cell: ({ ...props }) => {
        const name = props.data[props.row.index]?.name;

        const type = props.data[props.row.index]?.type?.name;

        const totalExercises = props.data[props.row.index]?.totalExercises;

        return (
          <div>
            <Typography style={{ textAlign: 'start' }} weight={700}>
              {name}
            </Typography>
            <SubTitle leftTitle={type ?? ''} rightTitle={`${totalExercises ?? 0} exercises`} />
          </div>
        );
      },
    },
  ];
};
